import type { SxProps, Theme } from '@mui/material';
import { sanitizeHref } from '@ovComponents/1-primative/link/externalLink';
import type { MouseEvent } from 'react';
import './chip.css';
import { Box, IconButton, Typography, useTheme } from 'hephaestus/web';
import { Close as CloseIcon, OpenInNew } from 'hephaestus/webIcons';
import { useChipTokens } from './chip.tokens';

export type ChipProps = {
  label?: string;
  text: string;
  leadingIcon?: any;
  small?: boolean;
  onClick?: (event: MouseEvent) => void;
  onRemove?: () => void;
  href?: string;
  sx?: SxProps<Theme>;
  'data-testid'?: string;
};

export const Chip = (props: ChipProps) => {
  const theme = useTheme();
  const tokens = useChipTokens(theme);
  const height = props.small ? 24 : 32;
  const safeHref = sanitizeHref(props.href);
  const isLink = Boolean(safeHref);
  const clickable = props.onClick !== undefined || isLink;
  const leadingIcon =
    props.leadingIcon ??
    (isLink ? <OpenInNew fontSize='inherit' aria-hidden /> : undefined);
  const chipStyle = {
    height: height - 2,
    margin: '2px',
    borderColor: tokens.borderColor,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderRadius: height / 2,
    backgroundColor: 'white',
    display: 'inline-flex',
    alignItems: 'center',
    width: 'max-content',
    paddingLeft: '8px',
    paddingRight: '0',
    textDecoration: 'none',
    color: 'inherit',
    boxSizing: 'border-box',
    ...(clickable && {
      cursor: 'pointer',
      userSelect: 'none',
      '&:hover': {
        backgroundColor: tokens.hoverColor,
        borderColor: tokens.hoverBorderColor,
      },
    }),
  };

  return (
    <Box
      component={isLink ? 'a' : 'div'}
      href={safeHref}
      target={isLink ? '_blank' : undefined}
      rel={isLink ? 'noopener noreferrer' : undefined}
      role={!isLink && clickable ? 'button' : undefined}
      tabIndex={!isLink && clickable ? 0 : undefined}
      aria-label={clickable ? props.text : undefined}
      data-testid={props['data-testid']}
      sx={{ ...chipStyle, ...props.sx } as any}
      onClick={props.onClick}
    >
      {leadingIcon && (
        <span
          className='ov-chip-leading-icon'
          style={{
            color: tokens.hoverBorderColor,
            transform: props.small ? 'scale(0.77)' : undefined,
            marginRight: 3,
          }}
        >
          {leadingIcon}
        </span>
      )}
      {props.label && (
        <Typography
          variant='labelSmall'
          sx={
            {
              color: tokens.labelColor,
              letterSpacing: '0px',
              paddingRight: '4px',
            } as any
          }
        >
          {props.label}
        </Typography>
      )}
      <Typography
        variant='labelSmall'
        sx={
          {
            color: tokens.textColor,
            letterSpacing: '0px',
            paddingRight: '8px',
          } as any
        }
      >
        {props.text}
      </Typography>
      {props.onRemove && (
        <IconButton
          sx={{ marginLeft: '-8px', padding: props.small ? '6px' : '8px' }}
          aria-label='close'
          data-testid={
            props['data-testid'] ? `${props['data-testid']}-remove` : undefined
          }
          onClick={(e) => {
            e.stopPropagation();
            if (props.onRemove) props.onRemove();
          }}
        >
          <CloseIcon
            sx={
              {
                color: tokens.textColor,
                width: props.small ? '12px' : '16px',
                height: props.small ? '12px' : '16px',
              } as any
            }
            fontSize='inherit'
          />
        </IconButton>
      )}
    </Box>
  );
};
